import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  adjuntarDex,
  readDam,
  readDim,
  readEP,
  readParte,
} from "../components/services/ReadFile";
import { validateStatus } from "../utils/validate";

const ReadQueueContext = createContext(null);
const STORAGE_KEY = "read-queue-state-v1";

const defaultQueueValue = {
  queue: [],
  pendingTasks: [],
  pendingCount: 0,
  isProcessing: false,
  runningTask: null,
  enqueueTask: () => null,
  enqueueTasks: () => [],
  removeTask: () => undefined,
  clearCompleted: () => undefined,
};

let queueStore = [];
let isProcessingStore = false;
let runningTaskStore = null;
let listeners = new Set();

function buildTaskId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeField(field = {}) {
  const rawName = field.name || field.label || "";
  const normalizedUrl = field.url || rawName || field.type || "";

  return {
    ...field,
    name: rawName,
    label: field.label || rawName,
    url: normalizedUrl,
    initial: field.initial ?? field.Value ?? false,
    validate: field.validate ?? false,
  };
}

function normalizeTask(task = {}) {
  return {
    id: task.id || buildTaskId(),
    status: task.status === "running" ? "pending" : task.status || "pending",
    createdAt: task.createdAt || Date.now(),
    ...task,
  };
}

function persistQueue(snapshot) {
  if (typeof window === "undefined") return;

  try {
    const serialized = JSON.stringify(snapshot);
    window.localStorage.setItem(STORAGE_KEY, serialized);
    console.log("[ReadQueue] Persisted queue", { storageKey: STORAGE_KEY, snapshot, serialized });
  } catch (error) {
    console.error("[ReadQueue] No se pudo persistir la cola de lectura", error);
  }
}

function buildSnapshot(queue = queueStore, processing = isProcessingStore, runningTask = runningTaskStore) {
  const pendingTasks = queue.filter((item) => item.status === "pending" || item.status === "running");

  return {
    queue,
    pendingTasks,
    pendingCount: pendingTasks.length,
    isProcessing: processing,
    runningTask,
    enqueueTask,
    enqueueTasks,
    removeTask,
    clearCompleted,
  };
}

function notifyListeners(nextQueue = queueStore, nextProcessing = isProcessingStore, nextRunningTask = runningTaskStore) {
  queueStore = nextQueue;
  isProcessingStore = nextProcessing;
  runningTaskStore = nextRunningTask;
  const snapshot = buildSnapshot(queueStore, isProcessingStore, runningTaskStore);
  listeners.forEach((listener) => listener(snapshot));
  return snapshot;
}

function enqueueTask(task) {
  const normalizedTask = normalizeTask({ ...task, status: "pending", createdAt: Date.now() });
  console.log("[ReadQueue] enqueueTask called", normalizedTask);

  const next = [...queueStore, normalizedTask];
  queueStore = next;
  persistQueue(next);
  console.log("[ReadQueue] Queue after enqueue", next);
  notifyListeners(next, isProcessingStore, runningTaskStore);

  if (!isProcessingStore) {
    console.log("[ReadQueue] Starting queue processing");
    processQueue();
  }

  return normalizedTask.id;
}

function enqueueTasks(tasks) {
  return tasks.map((task) => enqueueTask(task));
}

function updateTask(taskId, update) {
  const next = queueStore.map((item) => (item.id === taskId ? { ...item, ...update } : item));
  queueStore = next;
  persistQueue(next);
  notifyListeners(next, isProcessingStore, runningTaskStore);
}

function clearCompleted() {
  const next = queueStore.filter((item) => item.status !== "completed");
  queueStore = next;
  persistQueue(next);
  notifyListeners(next, isProcessingStore, runningTaskStore);
}

function removeTask(taskId) {
  const next = queueStore.filter((item) => item.id !== taskId);
  queueStore = next;
  persistQueue(next);
  notifyListeners(next, isProcessingStore, runningTaskStore);
}

function runReadTask(task) {
  return new Promise((resolve) => {
    const field = normalizeField(task.field);
    const procedure = task.procedure;
    console.log("[ReadQueue] runReadTask start", { task, field, procedureId: procedure?.id });

    const finish = (_index, _fields, result) => {
      const normalizedResult = Array.isArray(result) ? result : [result];
      console.log("[ReadQueue] runReadTask finish", { taskId: task.id, normalizedResult });
      resolve(normalizedResult);
    };

    if (!field || (!field.url && !field.name && !field.label)) {
      console.warn("[ReadQueue] Invalid field for task", task);
      finish(null, null, [{ readStatus: 500, readData: "Campo inválido", validate: true }]);
      return;
    }

    const processorKey = String(field.url || field.name || field.label || "").toLowerCase();
    console.log("[ReadQueue] Processor key", { taskId: task.id, processorKey, field });

    switch (processorKey) {
      case "adjuntardim":
        readDim([field], 0, procedure, [], finish);
        break;
      case "dimregularizada":
        readDim([field], 0, procedure, [], finish);
        break;
      case "adjuntardex":
        adjuntarDex([field], 0, procedure, [], finish);
        break;
      case "documentodam":
        readDam([field], 0, procedure, [], finish);
        break;
      case "partederecepcion":
        readParte([field], 0, procedure, [], finish);
        break;
      case "ep":
        readEP([field], 0, procedure, [], finish);
        break;
      default:
        finish(null, null, [{ readStatus: 500, readData: "Procesador no disponible", validate: true }]);
        break;
    }
  });
}

async function processQueue() {
  if (isProcessingStore) return;

  if (!queueStore.some((item) => item.status === "pending")) {
    isProcessingStore = false;
    notifyListeners(queueStore, false, runningTaskStore);
    return;
  }

  isProcessingStore = true;
  notifyListeners(queueStore, true, runningTaskStore);

  while (true) {
    const task = queueStore.find((item) => item.status === "pending");
    if (!task) break;

    console.log("[ReadQueue] Processing task", task);
    updateTask(task.id, { status: "running", errorMessage: null });
    const result = await runReadTask(task);
    const normalizedResult = Array.isArray(result) ? result : [result];
    const hasError = normalizedResult.some(
      (item) =>
        !item ||
        (!validateStatus(item.readStatus) && (item.validate || task.manualRead))
    );

    updateTask(task.id, {
      status: hasError ? "error" : "completed",
      result: normalizedResult,
      completedAt: Date.now(),
      errorMessage: hasError
        ? normalizedResult.find((item) => item?.readData)?.readData || "Error al procesar el documento"
        : null,
    });
  }

  isProcessingStore = false;
  notifyListeners(queueStore, false, runningTaskStore);
}

function hydrateQueueFromStorage() {
  if (typeof window === "undefined") return;

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    console.log("[ReadQueue] Initial storage load", { storageKey: STORAGE_KEY, saved });
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        const restoredQueue = parsed.map((item) => normalizeTask(item));
        console.log("[ReadQueue] Restored queue", restoredQueue);
        queueStore = restoredQueue;
        notifyListeners(restoredQueue, isProcessingStore, runningTaskStore);
      }
    }
  } catch (error) {
    console.error("[ReadQueue] No se pudo cargar la cola de lectura", error);
  }
}

export const ReadQueueProvider = ({ children }) => {
  const [value, setValue] = useState(() => buildSnapshot());

  useEffect(() => {
    hydrateQueueFromStorage();
    if (queueStore.some((item) => item.status === "pending")) {
      processQueue();
    }

    const listener = (snapshot) => setValue(snapshot);
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  return <ReadQueueContext.Provider value={value}>{children}</ReadQueueContext.Provider>;
};

export const useReadQueue = () => {
  const contextValue = useContext(ReadQueueContext);
  const [localState, setLocalState] = useState(() => buildSnapshot());

  useEffect(() => {
    const listener = (snapshot) => setLocalState(snapshot);
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  return contextValue || localState || defaultQueueValue;
};
